import type { IInitUser } from '@/store/user/interfaces'
import type { TypeLang } from '@/store/language/language'
import { StatusError } from './interfaces'
import { ApiResponse } from '@gry-dmitrij/api-request'
import request from './request'
import { uncodeSubscription } from '@/utils/auth/auth'

interface ILoginData {
  captcha: string,
  memberId: string,
  password: string,
  token: string
}

interface AuthApi {
  canBroadcast: true
  email: string
  isAdmin: boolean
  memberId: string
  phoneNumber: string
  subscribe: string
  directRegistration: boolean
}

interface LoginApi extends AuthApi {
  accessToken: string
}

export const login = async(userData: ILoginData): Promise<IInitUser | StatusError> => {
  const { data: userInfo }: ApiResponse<LoginApi> = await request('post', '/api/auth/login', userData)
  return { ...userInfo, subscribe: uncodeSubscription(userInfo.subscribe) }
}

interface ISsoData {
  memberId: string,
  otp: string
}

export const LOGIN_BY_SSO_PATH = '/api/auth/login-by-otp'

export const loginBySso = (data: ISsoData): Promise<IInitUser | StatusError> => {
  return request('post', LOGIN_BY_SSO_PATH, {
    ...data
  })
    .then(({ data }) => data)
}

export interface IAuthData {
  accessToken: string,
  memberId: string,
}

export const AUTHENTICATE_PATH = '/api/users/authenticate'

interface AuthApiResponse extends AuthApi {
  token: string
}

interface AuthResponse extends Omit<AuthApiResponse, 'subscribe'> {
  subscribe?: IInitUser['subscribe']
}

export const authenticate = async(authData: IAuthData): Promise<AuthResponse> => {
  const { data: userInfo }: ApiResponse<AuthApiResponse> = await request('post', AUTHENTICATE_PATH, authData)
  return { ...userInfo, subscribe: uncodeSubscription(userInfo.subscribe) }
}

interface CaptchaResponse {
  data: string,
  token: string
}

export const getCaptcha = (): Promise<CaptchaResponse> => {
  return request('post', '/api/auth/captcha')
    .then(data => {
      return data.data
    })
}

interface ILoginAsMember {
  memberId: string
}

export const loginAsMember = ({ memberId }: ILoginAsMember): Promise<IInitUser> => {
  return request('post', '/api/auth/login-as-member', { memberId })
    .then(data => {
      return data.data
    })
}

interface GetCodeProps {
  captcha: string
  captchaToken: string
  email: string
  lang: TypeLang
}

interface CodeResponse {
  token: string
}

export const getCode = async({ captcha, captchaToken, email, lang }: GetCodeProps): Promise<string> => {
  const { data }: ApiResponse<CodeResponse> = await request(
    'post',
    '/api/v2/signup',
    {
      captcha,
      token: captchaToken,
      email,
      lang
    }
  )
  return data.token
}

interface RepeatCodeProps {
  email: string
  token: string
  lang: TypeLang
}

export const repeatCode = async({ email, token, lang }: RepeatCodeProps): Promise<string> => {
  const { data }: ApiResponse<CodeResponse> = await request(
    'post',
    '/api/v2/signup/repeat',
    {
      email,
      token,
      lang
    }
  )
  return data.token
}

interface ConfirmCodeProps {
  email: string
  code: string
  token: string
}

export const confirmCode = async(
  {
    email,
    code,
    token
  }: ConfirmCodeProps
): Promise<void> => {
  return request(
    'post',
    '/api/v2/signup/email',
    {
      email,
      code,
      token
    }
  ).then()
}

interface CreateAccountProps {
  phone?: string
  code: string
  token: string
  password: string
  name: string
  email: string
  lang: TypeLang
}

export const createAccount = async(props: CreateAccountProps): Promise<IInitUser> => {
  const { data }: ApiResponse<IInitUser> = await request(
    'post',
    '/api/v2/signup/send',
    props
  )
  return data
}

interface RequestPasswordResetProps {
  memberId: string
  email: string
  captcha: string
  token: string
  lang: TypeLang
}

// Sends a password-reset link to the member's email.
export const requestPasswordReset = async(props: RequestPasswordResetProps): Promise<void> => {
  await request('post', '/api/forgot-password', props)
}

// Validates the reset token carried by the reset-password link. Resolves on a
// 200 response and rejects with an ApiError for any invalid/expired token.
export const validToken = async(token: string): Promise<void> => {
  await request('get', '/api/valid-token', { token })
}

interface ResetPasswordProps {
  memberId: string
  token: string
  password: string
}

// Sets a new password and returns the same payload as login() so the caller
// can authenticate the user right away.
export const resetPassword = async(
  { memberId, token, password }: ResetPasswordProps
): Promise<IInitUser | StatusError> => {
  const { data: userInfo }: ApiResponse<LoginApi> = await request('post', '/api/reset-password', {
    memberId,
    token,
    password
  })
  return { ...userInfo, subscribe: uncodeSubscription(userInfo.subscribe) }
}
